import { isValidEmail } from '../../../utils/utils';
import axios from '../../../utils/axios';
import { useIntl } from 'react-intl';
import { useEditTeamMember } from '../EditTeamMemberContext';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

export const useSaveUser = () => {
  const intl = useIntl();
  const t = id => intl.formatMessage({ id });
  const queryCache = useQueryClient();
  const {
    setErrors,
    setLoading,
    user,
    company,
    oldEmail,
    isDirty,
    companyLevelAcl,
    properties,
    globalAccess
  } = useEditTeamMember();
  const navigate = useNavigate();

  const saveUser = async () => {
    setLoading(false);
    setErrors([]);

    try {
      setLoading(true);

      // validation
      if (!isValidEmail(user.email)) {
        setErrors([t('error.email.valid')]);
        return;
      }

      // 'email', 'fname', 'lname', 'useMfa', 'companyTitle', 'companyAdmin', "status"
      let postobj = {
        fname: user.fname,
        lname: user.lname,
        useMfa: company.requireMfa ? true : user.useMfa,
        companyTitle: user.companyTitle.value,
        companyAdmin: user.companyAdmin,
        status: user.status.value
      };

      // updating email pulls an exist check api side
      // before updating db so only send it if it changed
      if (oldEmail !== user.email) postobj.email = user.email;

      let result1 = await axios.put(`/users/${user.username}`, postobj);

      let result2;
      if (isDirty) {

        // filter the properties to only send back acl data
        // and then post as batch to new endpoint

        const newAcls = properties.map(prop => {
          return {
            username: user.username,
            company: user.company,
            property: prop.name,
            roleid: prop?.roleId?.length > 0 ? prop.roleId : 'viewer',
            status: prop?.roleId?.length > 0 ? 'active' : 'inactive',
            objid: `${user.company}_${prop.name}`
          };
        });

        // Include company level ACL along with property ones for ease of filtering on backend
        postobj = {
          acls: [companyLevelAcl, ...newAcls],
          globalAccess
        };
        result2 = await axios.put(`/acls/batch-replace/${user.username}`, postobj);
      }

      if (result1.status === 200 && (!isDirty || result2.status === 200)) {
        await queryCache.invalidateQueries({queryKey: ['user', user.username]});
        navigate(`/users`);
      }
    } catch (e) {
      if (e.response && e.response.data) {
        setErrors(`${t('error.saveUser')} (${e.response.data.errors[0].message})`);
      } else {
        setErrors((e && e.message) || t('error.server'));
      }
      setLoading(false);
    }
  };

  return {
    saveUser
  };
};
